import { generateCodeChallenge, getSessionId } from "./tool";
import router from "@/router";
import { generateID } from "server/utils/tool";

const baseUrl = isDev ? import.meta.env.VITE_API_HOST : window.location.origin;
const csrfCache = new Map<string, { csrfToken: string; created: Date }>();

const baseFetch = async (
  url: string,
  method: string,
  params: any = {},
  isFile = false
): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      // let csrfTokenStr = "";
      // const fullPath = `/api/${url}`;
      // if (csrfCache.has(fullPath)) {
      //   const { csrfToken, created } = csrfCache.get(fullPath)!;
      //   const now = new Date();
      //   const diffMs = now.getTime() - created.getTime();
      //   // 未过期（ 15 分钟）
      //   if (diffMs < 900 * 1000) {
      //     csrfTokenStr = csrfToken;
      //   }
      // }
      // if (!csrfTokenStr) {
      //   csrfTokenStr = await getCsrfToken(fullPath);
      // }
      const headers: Record<string, string> = {
        // "X-Expected-State": localStorage.getItem("auth_state") || "",
        // "X-Code-Verifier": localStorage.getItem("code_verifier") || "",
        // "X-CSRF-Token": csrfTokenStr,
        "X-Session-Id": getSessionId(),
      };
      const isFormdata = params instanceof FormData;
      if (!isFormdata) {
        headers["Content-Type"] = "application/json";
      }
      const response = await fetch(`${baseUrl}/api/${url}`, {
        method,
        headers,
        body: method === "GET" ? null : isFormdata ? params : JSON.stringify(params),
        credentials: "include",
      });
      const contentType = response.headers.get("Content-Type");
      if (isFile && contentType?.includes("application/pdf")) {
        const blob = await response.blob();
        // 如果是文件下载，同时返回blob和文件名
        const disposition = response.headers.get("Content-Disposition");
        let filename = "download.pdf"; // 默认文件名

        if (disposition && disposition.indexOf("attachment") !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "").trim();
          }
        }

        resolve({ blob, filename });
      } else {
        const res: any = await response.json();
        if (res.error && res.innerStatus !== "Unauthorized") {
          message.error(res.error);
        }
        if (res.innerStatus === "Unauthorized") {
          window.dispatchEvent(
            new CustomEvent("unauthorized", {
              detail: { url: window.location.pathname },
            })
          );
        }
        if (res.error || res.innerStatus === "Unauthorized") {
          return reject(res.error);
        }
        if (res.message) {
          message.success(res.message);
        }
        resolve(res);
      }
    } catch (error) {
      return reject(error);
    }
  });
};

function clearCsrfCache() {
  csrfCache.clear();
}

/**
 * 恢复会话
 */
export async function restoreSession() {
  const { setUser } = useUserStore();
  const { setPlan } = usePlanStore();
  const { setConfig } = useReportConfigStore();
  const { setAdmin, setAdminApiPath } = useAdminStore();

  try {
    const { data, type, meta } = await get("auth/me");
    if (type === "admin") {
      setAdmin(data.admin);
    } else if (type === "user") {
      setUser(data.user);
      setPlan(data.plan);
      setConfig(data.config);
    }
    setAdminApiPath(meta.path);
  } catch (e) {
    setUser();
    setPlan();
    setConfig();
  }
}

/**
 * 请求csrf
 */
async function getCsrfToken(path: string) {
  try {
    const res = await fetch(`${baseUrl}/api/auth/csrf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
      credentials: "include",
    });
    const resData: Record<string, string> = await res.json();
    const { csrfToken } = resData;
    csrfCache.set(path, { csrfToken, created: new Date() });
    // 清理过期缓存
    for (const [key, { created }] of csrfCache) {
      const diffMs = new Date().getTime() - created.getTime();
      if (diffMs >= 3600 * 1000) {
        csrfCache.delete(key);
      }
    }
    return csrfToken;
  } catch (error) {
    return "";
  }
}

/**
 * 连接WebSocket
 * @param url
 * @param params
 * @param onMessage
 * @param onError
 * @returns
 */
export const connectWebSocket = (url: string): Promise<WebSocket> => {
  return new Promise((resolve, reject) => {
    const wsUrl = `${baseUrl.replace(/^http/, "ws")}/api/${url}/ws`;
    const ws = new WebSocket(wsUrl); // 依赖 Cookie 认证
    ws.onopen = () => resolve(ws);
    ws.onerror = () => reject("WebSocket connection failed");
  });
};

/**
 *  get请求
 * @param url 请求地址
 * @param params 请求参数
 * @returns
 */
export const get = async <T = any>(url: string, params: any = {}): Promise<T> =>
  baseFetch(url, "GET", params);

/**
 * post请求
 * @param url 请求地址
 * @param params  请求参数
 * @returns
 */
export const post = async <T = any>(url: string, params: any = {}, isFile = false): Promise<T> =>
  baseFetch(url, "POST", params, isFile);

/**
 * put请求
 * @param url 请求地址
 * @param params 请求参数
 * @returns
 */
export const put = async (url: string, params: any = {}) => baseFetch(url, "PUT", params);

/**
 * upload
 * @param url 请求地址
 * @param params 请求参数
 * @returns
 */
export const upload = async <T = any>(url: string, formData: any = {}): Promise<T> => {
  return baseFetch(url, "POST", formData);
};

/**
 * delete请求
 * @param url 请求地址
 * @param params 请求参数
 * @returns
 */
export const del = async (url: string, params: any = {}) => baseFetch(url, "DELETE", params);

export async function logout() {
  return get("auth/signout").then(() => {
    router.push({ path: "/login", query: { mode: "sign_in" } });
    clearCsrfCache();
    emitter.emit("user-logout");
  });
}

/**
 * 上报页面访问统计
 * @param pageName 页面名称
 */
export async function trackPageView(pageName: string) {
  try {
    const sessionId = getSessionId();
    await post("preflight/track-page-view", {
      pageName,
      sessionId,
    });
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
}

// 生成google授权URL
export async function getGoogleAuthUrl() {
  const state = generateID();
  localStorage.setItem("auth_state", state);
  const { clientId, scope, authUrl, redirectUri, redirectUriDev } = await get<any>("auth/googleSc");
  if (!clientId || !scope || !authUrl || !(redirectUri || redirectUriDev)) {
    return message.error("something went wrong");
  }
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: isDev ? redirectUriDev : redirectUri,
    response_type: "code",
    scope,
    access_type: "offline",
    prompt: "consent",
    state, // CSRF 防护，使用随机值
  });
  const url = `${authUrl}?${params.toString()}`;
  return { url, state };
}

export async function getTiktokAuthUrl() {
  const state = generateID();
  localStorage.setItem("auth_state", state);
  const { clientId, scope, redirectUri, authUrl } = await get<any>("auth/tiktokSc");
  if (!clientId || !scope || !authUrl || !redirectUri) return message.error("something went wrong");
  const params = new URLSearchParams({
    client_key: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: encodeURIComponent(scope),
    source: "web",
    state, // CSRF 防护，使用随机值
  });
  const url = `${authUrl}?${params.toString()}`;
  return { url, state };
}

export async function getLinkedinAuthUrl() {
  const state = generateID();
  localStorage.setItem("auth_state", state);
  const { clientId, scope, redirectUri, authUrl, redirectUriDev } =
    await get<any>("auth/linkedinSc");
  if (!clientId || !scope || !authUrl || !(redirectUri || redirectUriDev))
    return message.error("something went wrong");
  const url = new URL(authUrl);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", isDev ? redirectUriDev : redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("scope", scope);
  return { url: url.toString(), state };
}

export async function getXAuthUrl() {
  const { verifier, challenge } = await generateCodeChallenge();
  localStorage.setItem("code_verifier", verifier);
  // 生成随机 state 参数
  const state = generateID();
  localStorage.setItem("auth_state", state);
  const { clientId, scope, authUrl, redirectUri } = await get<any>("auth/xSc");
  if (!clientId || !scope || !authUrl || !redirectUri) return message.error("something went wrong");
  // 构造授权 URL，避免双重编码
  const encodedScope = scope.replace(/ /g, "%20"); // 手动将空格替换为 %20
  const params = [
    `client_id=${encodeURIComponent(clientId)}`,
    `redirect_uri=${encodeURIComponent(redirectUri)}`,
    `response_type=code`,
    `scope=${encodedScope}`, // 直接使用手动编码的 scope
    `state=${encodeURIComponent(state)}`,
    `code_challenge=${challenge}`,
    `code_challenge_method=S256`,
  ];
  const url = `${authUrl}?${params.join("&")}`;
  return { url, state };
}
